<template>
  <PageHeader :showBreadcrumb="true" />
  <h2>
    Licensing Updates & Other Required Reporting
  </h2>
  <div class="d-flex justify-content-center align-items-center container" style="flex-direction: column">
    <div class="flex-row">
      <P style="margin-right: 5rem;margin-left: 5rem">
        Under Minnesota Statue §62D.08 Subd. 1, every health maintenance organization (HMO)
        and county-based purchaser (CBP) needs to report modifications of several operations
        documents that were submitted as part of the initial HMO or CBP application. In addition,
        HMOs are required to report on cybersecurity events and provide collaboration plans
        or collaboration plan updates every two years. All this information can be submitted
        via this portal module.
        <br/>
        For licensing updates, MDH will respond within 60 days regarding your submission.
        <br/>
        For more information on licensing updates and other required reporting, please visit our website,<br/>
        <a href="https://www.health.state.mn.us/facilities/insurance/managedcare/annualreporting.html"
           target="_blank">Health Plan Reporting Requirements(www.health.mn.us)</a>
      </P>
    </div>
    <div class="flex-row">
      <h3>Please note:</h3>
      <ul>
        <li> You may upload a cover letter rather than providing an explanation with your submission.
          Multiple documents may be uploaded as part of a submission.</li>
        <li> If you have made changes to a licensing document, please upload both
          a redline copy that clearly shows where changes were made, and a clean copy.
          MDH may contact you for more information during the review period.</li>
        <li> Unless otherwise noted, there is a $125 filing fee for each licensing update;
          this will be collected as part of MDH’s quarterly invoicing.
          SERFF change requests are still submitted via email.</li>
        <li> Changes to Service Area are submitted through the Network Adequacy section of
          the MCS Portal – please click “Home” in the upper left corner of
          this page to get to that section.
        </li>
      </ul>
    </div>
    <div class="flex-row justify-content-center align-items-center" style="padding-bottom: 2em">
      <form @submit.stop.prevent="onSubmit">
        <!-- Display the user entities -->
        <div class="col-lg-12 col-md-12">
          <FormSelectGroup v-if="userEntityOptions"
                           label="Select Entity"
                           inputId="entity-input"
                           :required="true"
                           state>
            <template #default="">
              <PvSelect id="entity-input"
                        v-model="selectedEntity"
                        :options="userEntityOptions"
                        @change="onChangeEntity"
                        ariaLabel="entity-input-label"
                        placeholder="-- Select Entity --"
                        optionLabel="name"
                        optionValue="id"/>
            </template>
          </FormSelectGroup>
        </div>
        <div v-if="selectedEntity!=null" class="col-lg-12 col-md-12" style="padding-bottom: 3em">
          <FormSelectGroup label="Which licensing document(s) are you updating?"
                           inputId="document-input"
                           :required="true"
                           state>
            <template #default="">
              <MultiSelect
                v-model="selectedUploaders"
                :options="uploadersOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Licensing Document(s)"
                :max-selected-labels="1"
                :showClear="false"
                :select-all="false"
                :show-toggle-all="false"
                class="w-auto"
              />
            </template>
          </FormSelectGroup>
        </div>

        <div v-for="option in selectedUploaders" :key="option" class="option-content">
          <div class="col-lg-12 col-md-12" v-if="option === 'Other'">
            <FormSelectGroup label="Other Document Type?"
                             inputId="other-input"
                             state>
              <template #default="">
                <input-text
                  id="customOtherInput"
                  type="text"
                  v-model="otherValue"
                  :required = false
                  placeholder="Input Other Document Type here..."
                />
              </template>
            </FormSelectGroup>
          </div>
        </div>

        <fieldset v-if="selectedUploaders.includes('Articles of Incorporation')">
          <legend>Articles of Incorporation (MN Statutes 62D.03, Subd. 4 (a))</legend>
          <MnRow>
            <MnColumn cols="6">
              <ul>
                <li>Instructions: Please upload both a redline copy that clearly shows where changes were made, and a clean copy.</li>
                <li>Document types: PDF, Word, picture (.png/.jpg/.tiff etc), excel</li>
                <li>File size may not exceed 10 MB.</li>
                <li>Up to a maximum number of 20 uploads.</li>
              </ul>
              <p>Briefly explain the changes that were made to this document or explain the reason why you are uploading this document.</p>
              <p>You may also upload a cover letter that summarizes changes</p>
              <div>
                <label for="explanationText"></label>
                <textarea id="explanationText"
                            v-model="explain"
                            :maxlength="maxChar"
                            placeholder="Enter Explanations"
                />
                <div>Remaining Characters: {{remaingChar}}</div>
              </div>

              <MNFileUpload
                  module-type="Articles of Incorporation Documents"
                  label="Articles of Incorporation Documents"
                  :entity="selectedEntity"
                  ref="articlesIncorporationDocuments"
                  name="articlesOfIncorporation"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  :fileLimit="20"
                  />
            </MnColumn>
          </MnRow>
        </fieldset>

        <fieldset v-if="selectedUploaders.includes('Bylaws')">
          <legend>Bylaws (MN Statutes 62D.03, Subd. 4 (b))</legend>
          <MnRow>
            <MnColumn cols="6">
              <ul>
                <li>Instructions: Please upload both a redline copy that clearly shows where changes were made, and a clean copy.</li>
                <li>Document types: PDF, Word, picture (.png/.jpg/.tiff etc), excel</li>
                <li>File size may not exceed 10 MB.</li>
                <li>Up to a maximum number of 20 uploads.</li>
              </ul>
              <p>Briefly explain the changes that were made to this document or explain the reason why you are uploading this document.</p>
              <p>You may also upload a cover letter that summarizes changes</p>
              <div>
                <label for="explanationBylawText"></label>
                <textarea id="explanationBylawText" v-model="bylawText"
                          :maxlength="maxChar"
                          placeholder="Enter Explanations"
                />
                <div>Remaining Characters: {{bylawRemain}}</div>
              </div>
              <MNFileUpload
                  module-type="Bylaws Documents"
                  label="Bylaws Documents"
                  :entity="selectedEntity"
                  ref="bylawsDocuments"
                  name="bylawsDocuments"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  :fileLimit="20"/>
            </MnColumn>
          </MnRow>
        </fieldset>

        <fieldset v-if="selectedUploaders.includes('Conflict of Interest Policy')">
          <legend>Conflict of Interest Policy (MN Statutes 62D.03, Subd. 4 (r))</legend>
          <MnRow>
            <MnColumn cols="6">
              <ul>
                <li>Instructions: Please upload both a redline copy that clearly shows where changes were made, and a clean copy.</li>
                <li>Document types: PDF, Word, picture (.png/.jpg/.tiff etc), excel</li>
                <li>File size may not exceed 10 MB.</li>
                <li>Up to a maximum number of 20 uploads.</li>
              </ul>
              <p>Briefly explain the changes that were made to this document or explain the reason why you are uploading this document.</p>
              <p>You may also upload a cover letter that summarizes changes</p>
              <div>
                <label for="explanationConflictText"></label>
                <textarea id="explanationConflictText"
                          v-model="conflictText"
                          :maxlength="maxChar"
                          placeholder="Enter Explanations"
                />
                <div>Remaining Characters: {{conflictRemain}}</div>
              </div>
              <MNFileUpload
                  module-type="Conflict of Interest Policy Documents"
                  label="Conflict of Interest Policy Documents"
                  :entity="selectedEntity"
                  ref="conflictOfInterestPolicyDocuments"
                  name="conflictOfInterestPolicyDocuments"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  :fileLimit="20"/>
            </MnColumn>
          </MnRow>
        </fieldset>

      </form>
    </div>
  </div>
</template>
<script setup>
  import {useAppStore} from '@/stores/appStore';
  import {ENTITY_TYPES, HMO_CDP_UPDATE_DOCUMENTS, ICONS} from "@/utils/constants.js";
  import {computed, ref} from "vue";
  import {useAuthStore} from "@/stores/authStore.js";
  import FormSelectGroup from "@/components/common/FormSelectGroup.vue";
  import {MultiSelect,InputText} from "primevue";
  import PageHeader from "@/components/common/PageHeader.vue";
  import MnRow from "@/components/common/MnRow.vue";
  import MnColumn from "@/components/common/MnColumn.vue";
  import MNFileUpload from "@/components/common/MNFileUpload.vue";

  const appStore = useAppStore();
  const userStore = useAuthStore();

  const selectedEntity = ref();
  const selectedUploaders = ref([]);
  const otherValue = ref();
  const otherCheck = "Other";
  const remaingChar = computed(()=>maxChar-explain.value.length)
  const bylawRemain = computed(()=>maxChar- bylawText.value.length)
  const conflictRemain =computed(()=>maxChar - conflictText.value.length)

  let isArticleVisible = ref(false);
  let isTextExplainVisible = ref(false);
  let isTextBylawsVisible = ref(false);
  let isBylawsVisible = ref(false);
  let isTextConflictVisible = ref(false);
  let isConflictVisible = ref(false);
  let explain = ref('');
  let maxChar = 3000;
  let bylawText = ref('');
  let conflictText = ref('');
  let articleDoc = ref('License_AIC_hmo_date_page')

  const toggleArticles = ()=>{
    isArticleVisible = !isArticleVisible;
  };

  const toggleConflict = ()=>{
    isConflictVisible = !isConflictVisible;
  };

  const toggleConflictText = ()=>{
    isTextConflictVisible = !isTextConflictVisible;
  };

  const toggleBylaws = ()=>{
    isArticleVisible = !isArticleVisible;
  };

  const toggleBylawsText = ()=>{
    isTextBylawsVisible = !isTextBylawsVisible;
  };

  const toggleExplain = ()=>{
    isTextExplainVisible = !isTextExplainVisible;
  };

  const uploadersOptions = computed(() => {
    return Object.entries(HMO_CDP_UPDATE_DOCUMENTS).map(([k, v]) => ({
      code: k,
      label: v,
      value: v
    }));
  });

  const userEntityOptions = computed(() => {
    return appStore.getUserEntitiesByTypes([ENTITY_TYPES.HMO, ENTITY_TYPES.CBP]);
  });
</script>

<style scoped>
.tx-p {
  padding: 1.5rem;
  height: 200px;
}
</style>